import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../../../context/AuthContext";
import { getAiAssistantQuestionSet } from "../../../constants/aiAssistantQuestionCatalog";
import {
  endChatConversation,
  saveChatFeedback,
  sendChatMessage,
} from "../../../services/Chat";
import {
  clearChatConversationSession,
  getChatConversationFeedback,
  getChatConversationToken,
  getOrCreateChatConversationToken,
  saveChatConversationFeedback,
} from "../../../services/ChatSession";
import "./ChatWidget.css";

const TYPING_INTERVAL_MS = 30;
const MAX_TYPING_DURATION_MS = 6000;
const RESPONSE_DELAY_MS = 250;
const CHAT_VIEW = {
  CONVERSATION: "conversation",
  QUESTIONS: "questions",
};
const GENERIC_ERROR_MESSAGE =
  "Asystent jest chwilowo niedostępny. Spróbuj ponownie.";

const createMessage = (role, text) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  role,
  text,
  createdAt: new Date().toISOString(),
});

const getMessageDate = (message) => {
  if (message.createdAt) {
    const createdAtDate = new Date(message.createdAt);
    if (!Number.isNaN(createdAtDate.getTime())) {
      return createdAtDate;
    }
  }

  const idTimestamp = Number(String(message.id).split("-")[0]);
  const idDate = new Date(idTimestamp);
  return Number.isNaN(idDate.getTime()) ? null : idDate;
};

const formatMessageTime = (date) =>
  date?.toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
  }) || "";

const formatQuestionCount = (count) => {
  if (count === 1) return "1 pytanie";
  if (count >= 2 && count <= 4) return `${count} pytania`;
  return `${count} pytań`;
};

const readHistory = (storageKey, conversationToken) => {
  if (!conversationToken) return [];

  try {
    const stored = sessionStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : null;

    if (parsed?.conversationToken !== conversationToken) return [];

    return Array.isArray(parsed.messages)
      ? parsed.messages.filter(
          (message) =>
            message &&
            typeof message.id === "string" &&
            ["user", "assistant", "error"].includes(message.role) &&
            typeof message.text === "string",
        )
      : [];
  } catch {
    return [];
  }
};

const copyMessageText = async (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();

  try {
    if (!document.execCommand("copy")) {
      throw new Error("Przeglądarka odrzuciła kopiowanie wiadomości.");
    }
  } finally {
    textArea.remove();
  }
};

function ChatWidget({ applicationId, statusId, statusName }) {
  const { accessToken, userId, userName } = useAuth();
  const conversationScope = useMemo(
    () => `application:${applicationId}:status:${statusId}`,
    [applicationId, statusId],
  );
  const questionSet = useMemo(
    () => getAiAssistantQuestionSet(statusId),
    [statusId],
  );
  const displayedStatusName =
    questionSet.statusName !== "Nieznany"
      ? questionSet.statusName
      : statusName || "Nieznany";
  const [conversationToken, setConversationToken] = useState(() =>
    getChatConversationToken(userId, conversationScope),
  );
  const storageKey = useMemo(
    () => `kig-chat-history:${userId || "current-user"}:${conversationScope}`,
    [conversationScope, userId],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() =>
    readHistory(storageKey, conversationToken),
  );
  const [activeView, setActiveView] = useState(() =>
    messages.length > 0 ? CHAT_VIEW.CONVERSATION : CHAT_VIEW.QUESTIONS,
  );
  const [isTyping, setIsTyping] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [conversationFeedback, setConversationFeedback] = useState(() =>
    getChatConversationFeedback(conversationToken),
  );
  const [feedbackComment, setFeedbackComment] = useState(
    conversationFeedback.comment,
  );
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [isEndConversationConfirmOpen, setIsEndConversationConfirmOpen] =
    useState(false);
  const [isEndingConversation, setIsEndingConversation] = useState(false);
  const [isEndConversationFeedbackStep, setIsEndConversationFeedbackStep] =
    useState(false);
  const [isSavingEndFeedback, setIsSavingEndFeedback] = useState(false);
  const [endConversationError, setEndConversationError] = useState("");
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const isTypingRef = useRef(false);
  const typingTimerRef = useRef(null);
  const typingMessageIdRef = useRef(null);
  const responseDelayRef = useRef(null);
  const copyStatusTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const launcherRef = useRef(null);
  const headerMenuRef = useRef(null);
  const conversationScopeRef = useRef(conversationScope);
  conversationScopeRef.current = conversationScope;
  const lastCompletedAssistantMessageIndex = messages.reduce(
    (lastIndex, message, index) =>
      message.role === "assistant" &&
      message.text.trim() &&
      message.id !== typingMessageIdRef.current
        ? index
        : lastIndex,
    -1,
  );
  const hasCompletedAssistantResponse = lastCompletedAssistantMessageIndex >= 0;

  useEffect(() => {
    const feedback = getChatConversationFeedback(conversationToken);
    setConversationFeedback(feedback);
    setFeedbackComment(feedback.comment);
  }, [conversationToken]);

  useEffect(() => {
    const storedConversationToken = getChatConversationToken(
      userId,
      conversationScope,
    );
    const storedMessages = readHistory(storageKey, storedConversationToken);
    setConversationToken(storedConversationToken);
    setMessages(storedMessages);
    setActiveView(
      storedMessages.length > 0
        ? CHAT_VIEW.CONVERSATION
        : CHAT_VIEW.QUESTIONS,
    );
  }, [conversationScope, storageKey, userId]);

  useEffect(() => {
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (responseDelayRef.current) {
      clearTimeout(responseDelayRef.current);
      responseDelayRef.current = null;
    }
    typingMessageIdRef.current = null;
    isTypingRef.current = false;
    setIsTyping(false);
    setExpandedCategoryId(null);
  }, [conversationScope]);

  useEffect(() => {
    if (!conversationToken) return;
    if (
      getChatConversationToken(userId, conversationScope) !== conversationToken
    ) {
      return;
    }

    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        conversationToken,
        messages,
      }),
    );
  }, [conversationScope, conversationToken, messages, storageKey, userId]);

  useEffect(() => {
    if (isOpen && activeView === CHAT_VIEW.CONVERSATION) {
      messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
    }
  }, [activeView, isOpen, messages]);

  useEffect(() => {
    if (!isHeaderMenuOpen) return undefined;

    const closeMenuOnOutsideClick = (event) => {
      if (!headerMenuRef.current?.contains(event.target)) {
        setIsHeaderMenuOpen(false);
      }
    };

    const closeMenuOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsHeaderMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenuOnOutsideClick);
    document.addEventListener("keydown", closeMenuOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeMenuOnOutsideClick);
      document.removeEventListener("keydown", closeMenuOnEscape);
    };
  }, [isHeaderMenuOpen]);

  useEffect(() => {
    if (!isEndConversationConfirmOpen) return undefined;

    const closeConfirmationOnEscape = (event) => {
      if (event.key === "Escape" && !isEndingConversation) {
        setIsEndConversationConfirmOpen(false);
        setEndConversationError("");
      }
    };

    document.addEventListener("keydown", closeConfirmationOnEscape);
    return () =>
      document.removeEventListener("keydown", closeConfirmationOnEscape);
  }, [isEndConversationConfirmOpen, isEndingConversation]);

  useEffect(
    () => () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
      }
      if (responseDelayRef.current) {
        clearTimeout(responseDelayRef.current);
      }
      if (copyStatusTimerRef.current) {
        clearTimeout(copyStatusTimerRef.current);
      }
    },
    [],
  );

  const typeResponse = (role, text) => {
    const responseMessage = createMessage(role, "");
    const typingStartedAt = Date.now();
    const typingDuration = Math.min(
      MAX_TYPING_DURATION_MS,
      Math.max(TYPING_INTERVAL_MS, text.length * TYPING_INTERVAL_MS),
    );
    let characterIndex = 0;

    typingMessageIdRef.current = responseMessage.id;
    setMessages((current) => [...current, responseMessage]);

    const renderNextCharacters = () => {
      const elapsedTime = Date.now() - typingStartedAt;
      const progress = Math.min(1, elapsedTime / typingDuration);
      characterIndex = Math.min(
        text.length,
        Math.max(characterIndex + 1, Math.ceil(text.length * progress)),
      );

      setMessages((current) =>
        current.map((message) =>
          message.id === responseMessage.id
            ? { ...message, text: text.slice(0, characterIndex) }
            : message,
        ),
      );

      if (characterIndex >= text.length) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        typingMessageIdRef.current = null;
        isTypingRef.current = false;
        setIsTyping(false);
      }
    };

    typingTimerRef.current = window.setInterval(
      renderNextCharacters,
      TYPING_INTERVAL_MS,
    );
    renderNextCharacters();
  };

  const respondToQuestion = async (
    question,
    userSession,
    requestConversationScope,
  ) => {
    try {
      const answer = await sendChatMessage(
        question,
        userSession,
        applicationId,
        accessToken,
      );
      if (conversationScopeRef.current !== requestConversationScope) return;
      typeResponse("assistant", answer);
    } catch (error) {
      console.error("Błąd odpowiedzi AI", {
        status: error?.response?.status,
        backendMessage: error?.response?.data?.msg,
        errorCode: error?.response?.data?.error_code,
        message: error?.message,
      });
      if (conversationScopeRef.current !== requestConversationScope) return;
      typeResponse("error", GENERIC_ERROR_MESSAGE);
    }
  };

  const submitQuestion = (selectedQuestion) => {
    if (
      isTypingRef.current ||
      !selectedQuestion ||
      !questionSet.questions.some(
        (question) => question.id === selectedQuestion.id,
      )
    ) {
      return;
    }

    const question = selectedQuestion.text.trim();
    if (!question) return;

    const userMessage = createMessage("user", question);
    const activeConversationToken = getOrCreateChatConversationToken(
      userId,
      conversationScope,
    );
    const startsNewConversation = activeConversationToken !== conversationToken;

    isTypingRef.current = true;
    setIsTyping(true);
    setExpandedCategoryId(null);
    setActiveView(CHAT_VIEW.CONVERSATION);
    setConversationToken(activeConversationToken);
    setMessages((current) =>
      startsNewConversation ? [userMessage] : [...current, userMessage],
    );
    responseDelayRef.current = window.setTimeout(() => {
      responseDelayRef.current = null;
      respondToQuestion(
        question,
        activeConversationToken,
        conversationScope,
      );
    }, RESPONSE_DELAY_MS);
  };

  const handleRetryQuestion = (question) => {
    if (isTypingRef.current || typeof question !== "string") return;

    const predefinedQuestion = questionSet.questions.find(
      (item) => item.text === question,
    );
    submitQuestion(predefinedQuestion);
  };

  const handleCopyMessage = async (message) => {
    if (!message?.text?.trim()) return;

    try {
      await copyMessageText(message.text);
      setCopiedMessageId(message.id);

      if (copyStatusTimerRef.current) {
        clearTimeout(copyStatusTimerRef.current);
      }
      copyStatusTimerRef.current = window.setTimeout(() => {
        setCopiedMessageId(null);
        copyStatusTimerRef.current = null;
      }, 1600);
    } catch (error) {
      console.error("Nie udało się skopiować wiadomości:", error);
    }
  };

  const collapseChat = () => {
    setIsHeaderMenuOpen(false);
    setIsEndConversationConfirmOpen(false);
    setIsEndConversationFeedbackStep(false);
    setEndConversationError("");
    setIsOpen(false);
    window.setTimeout(() => launcherRef.current?.focus(), 0);
  };

  const openEndConversationConfirmation = () => {
    if (isTypingRef.current) return;

    setIsHeaderMenuOpen(false);
    setIsEndConversationFeedbackStep(false);
    setEndConversationError("");
    setIsEndConversationConfirmOpen(true);
  };

  const handleEndConversation = async () => {
    if (!conversationToken || isEndingConversation || isTypingRef.current) {
      return;
    }

    setIsEndingConversation(true);
    setEndConversationError("");

    try {
      await endChatConversation(conversationToken, accessToken);
      clearChatConversationSession(
        userId,
        conversationToken,
        conversationScope,
      );
      sessionStorage.removeItem(storageKey);

      setConversationToken(null);
      setMessages([]);
      setIsTyping(false);
      setExpandedCategoryId(null);
      setActiveView(CHAT_VIEW.QUESTIONS);
      isTypingRef.current = false;
      typingMessageIdRef.current = null;
      setConversationFeedback(getChatConversationFeedback(null));
      setFeedbackComment("");
      setIsFeedbackFormOpen(false);
      setIsEndConversationFeedbackStep(false);
      setIsEndConversationConfirmOpen(false);
      setIsOpen(false);
      window.setTimeout(() => launcherRef.current?.focus(), 0);
    } catch {
      setEndConversationError(
        "Nie udało się zakończyć rozmowy. Proszę spróbować ponownie.",
      );
    } finally {
      setIsEndingConversation(false);
    }
  };

  const handleConfirmEndConversation = () => {
    if (!conversationFeedback.rating) {
      setEndConversationError("");
      setIsEndConversationFeedbackStep(true);
      return;
    }

    handleEndConversation();
  };

  const handleEndConversationAfterFeedback = async () => {
    if (
      !conversationFeedback.rating ||
      isSavingEndFeedback ||
      isEndingConversation
    ) {
      return;
    }

    const nextFeedback = saveChatConversationFeedback(conversationToken, {
      rating: conversationFeedback.rating,
      comment:
        conversationFeedback.rating === "negative" ? feedbackComment : "",
    });
    setConversationFeedback(nextFeedback);
    setFeedbackComment(nextFeedback.comment);
    setIsSavingEndFeedback(true);
    setEndConversationError("");

    try {
      await saveChatFeedback(
        conversationToken,
        {
          rating: nextFeedback.rating,
          comment: nextFeedback.comment,
        },
        accessToken,
      );
      await handleEndConversation();
    } catch {
      setEndConversationError(
        "Nie udało się zapisać oceny. Proszę spróbować ponownie.",
      );
    } finally {
      setIsSavingEndFeedback(false);
    }
  };

  const persistFeedback = (feedback) => {
    saveChatFeedback(
      conversationToken,
      { rating: feedback.rating, comment: feedback.comment },
      accessToken,
    ).catch((error) => {
      console.error("Nie udało się zapisać opinii o rozmowie:", error);
    });
  };

  const handleFeedbackRating = (rating) => {
    const nextFeedback = saveChatConversationFeedback(conversationToken, {
      rating,
      comment:
        rating === "negative" && conversationFeedback.rating === "negative"
          ? conversationFeedback.comment
          : "",
    });

    setConversationFeedback(nextFeedback);
    setFeedbackComment(nextFeedback.comment);
    setIsFeedbackFormOpen(rating === "negative");
    persistFeedback(nextFeedback);
  };

  const handleFeedbackSubmit = (event) => {
    event.preventDefault();
    const nextFeedback = saveChatConversationFeedback(conversationToken, {
      rating: "negative",
      comment: feedbackComment,
    });

    setConversationFeedback(nextFeedback);
    setFeedbackComment(nextFeedback.comment);
    setIsFeedbackFormOpen(false);
    persistFeedback(nextFeedback);
  };

  const renderQuestionButton = (question, variant = "default") => (
    <button
      key={question.id}
      type="button"
      className={`kig-chat__question kig-chat__question--${variant}`}
      onClick={() => submitQuestion(question)}
      disabled={isTyping}
    >
      <span>{question.text}</span>
      <i className="bi bi-arrow-right" aria-hidden="true" />
    </button>
  );
  const selectedCategory = questionSet.categories.find(
    (category) => category.id === expandedCategoryId,
  );

  return (
    <div className="kig-chat">
      {isOpen && (
        <section
          id="kig-chat-panel"
          className="kig-chat__panel"
          aria-label="Pomoc AI"
          aria-live="polite"
        >
          <header className="kig-chat__header">
            <div className="kig-chat__avatar" aria-hidden="true">
              <i className="bi bi-stars" />
            </div>
            <div className="kig-chat__heading">
              <h2 className="kig-chat__title">Pomoc AI</h2>
              <span className="kig-chat__stage">
                Etap wniosku: {displayedStatusName}
              </span>
            </div>
            <div className="kig-chat__header-actions">
              {conversationToken && messages.length > 0 && (
                <div
                  ref={headerMenuRef}
                  className="kig-chat__header-menu-wrapper"
                >
                  <button
                    type="button"
                    className="kig-chat__header-action"
                    onClick={() => setIsHeaderMenuOpen((current) => !current)}
                    aria-label="Więcej opcji rozmowy"
                    aria-haspopup="menu"
                    aria-expanded={isHeaderMenuOpen}
                  >
                    <i className="bi bi-three-dots-vertical" />
                  </button>
                  {isHeaderMenuOpen && (
                    <div className="kig-chat__header-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={openEndConversationConfirmation}
                        disabled={isTyping}
                      >
                        <i
                          className="bi bi-box-arrow-right"
                          aria-hidden="true"
                        />
                        Zakończ rozmowę
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                className="kig-chat__close"
                onClick={collapseChat}
                aria-label="Zwiń czat"
                aria-controls="kig-chat-panel"
                title="Zwiń czat"
              >
                <i className="bi bi-chevron-down" />
              </button>
            </div>
          </header>

          {activeView === CHAT_VIEW.CONVERSATION && (
            <div
              className="kig-chat__messages"
              aria-label="Historia rozmowy"
            >
            {messages.map((message, index) => {
              const isLastTypingMessage =
                isTyping &&
                message.id === typingMessageIdRef.current &&
                index === messages.length - 1;
              const messageDate = getMessageDate(message);
              const messageTime = formatMessageTime(messageDate);

              return (
                <div
                  key={message.id}
                  className={`kig-chat__message-row kig-chat__message-row--${message.role}`}
                >
                  {message.role === "user" && (
                    <span className="kig-chat__message-author kig-chat__message-author--user">
                      {userName || "Użytkownik"}
                    </span>
                  )}
                  <div
                    className={`kig-chat__message kig-chat__message--${message.role} ${
                      isLastTypingMessage ? "kig-chat__message--typing" : ""
                    }`}
                  >
                    {message.role === "assistant" && !isLastTypingMessage ? (
                      <ReactMarkdown
                        components={{
                          a: ({ node, children, ...props }) => (
                            <a
                              {...props}
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {message.text}
                      </ReactMarkdown>
                    ) : (
                      message.text
                    )}
                    {isLastTypingMessage && (
                      <span className="kig-chat__typing-cursor" />
                    )}
                  </div>
                  {!isLastTypingMessage && message.text.trim() && (
                    <div className="kig-chat__message-actions">
                      {messageTime && (
                        <time
                          className="kig-chat__message-time"
                          dateTime={messageDate.toISOString()}
                          aria-label={"Wysłano o " + messageTime}
                        >
                          {messageTime}
                        </time>
                      )}
                      {message.role !== "error" && (
                        <>
                          {message.role === "user" && (
                            <button
                              type="button"
                              className="kig-chat__message-action"
                              onClick={() => handleRetryQuestion(message.text)}
                              disabled={isTyping}
                              aria-label="Wyślij tę wiadomość ponownie"
                              title="Ponów wiadomość"
                            >
                              <i
                                className="bi bi-arrow-clockwise"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                          <button
                            type="button"
                            className="kig-chat__message-action"
                            onClick={() => handleCopyMessage(message)}
                            aria-label={
                              copiedMessageId === message.id
                                ? "Wiadomość skopiowana"
                                : "Kopiuj wiadomość"
                            }
                            title={
                              copiedMessageId === message.id
                                ? "Skopiowano"
                                : "Kopiuj wiadomość"
                            }
                          >
                            <i
                              className={
                                copiedMessageId === message.id
                                  ? "bi bi-check-lg"
                                  : "bi bi-clipboard"
                              }
                              aria-hidden="true"
                            />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {isTyping && !typingMessageIdRef.current && (
              <div className="kig-chat__message-row kig-chat__message-row--assistant">
                <div className="kig-chat__message kig-chat__message--assistant kig-chat__waiting">
                  <span className="kig-chat__waiting-text">
                    Trwa przygotowywanie odpowiedzi
                  </span>
                  <span className="kig-chat__waiting-dot" />
                  <span className="kig-chat__waiting-dot" />
                  <span className="kig-chat__waiting-dot" />
                </div>
              </div>
            )}
            {hasCompletedAssistantResponse && !isTyping && (
              <aside
                className="kig-chat__feedback"
                aria-label="Ocena rozmowy z pomocą AI"
              >
                <div className="kig-chat__feedback-row">
                  <span className="kig-chat__feedback-question">
                    Czy ta rozmowa była pomocna?
                  </span>
                  <div className="kig-chat__feedback-actions">
                    <button
                      type="button"
                      className={`kig-chat__feedback-rating ${
                        conversationFeedback.rating === "positive"
                          ? "kig-chat__feedback-rating--active"
                          : ""
                      }`}
                      onClick={() => handleFeedbackRating("positive")}
                      aria-label="Oceń rozmowę pozytywnie"
                      aria-pressed={conversationFeedback.rating === "positive"}
                      title="Rozmowa pomocna"
                    >
                      <i
                        className={`bi ${
                          conversationFeedback.rating === "positive"
                            ? "bi-hand-thumbs-up-fill"
                            : "bi-hand-thumbs-up"
                        }`}
                      />
                    </button>
                    <button
                      type="button"
                      className={`kig-chat__feedback-rating ${
                        conversationFeedback.rating === "negative"
                          ? "kig-chat__feedback-rating--active kig-chat__feedback-rating--negative"
                          : ""
                      }`}
                      onClick={() => handleFeedbackRating("negative")}
                      aria-label="Oceń rozmowę negatywnie"
                      aria-pressed={conversationFeedback.rating === "negative"}
                      title="Rozmowa wymaga poprawy"
                    >
                      <i
                        className={`bi ${
                          conversationFeedback.rating === "negative"
                            ? "bi-hand-thumbs-down-fill"
                            : "bi-hand-thumbs-down"
                        }`}
                      />
                    </button>
                  </div>
                  {conversationFeedback.rating === "negative" &&
                    conversationFeedback.comment &&
                    !isFeedbackFormOpen && (
                      <button
                        type="button"
                        className="kig-chat__feedback-trigger"
                        onClick={() => setIsFeedbackFormOpen(true)}
                      >
                        Edytuj komentarz
                      </button>
                    )}
                </div>

                {isFeedbackFormOpen &&
                  conversationFeedback.rating === "negative" && (
                    <form
                      className="kig-chat__feedback-form"
                      onSubmit={handleFeedbackSubmit}
                    >
                      <div className="kig-chat__feedback-form-header">
                        <label htmlFor="kig-chat-feedback">
                          Co możemy poprawić?
                        </label>
                        <button
                          type="button"
                          className="kig-chat__feedback-form-close"
                          onClick={() => setIsFeedbackFormOpen(false)}
                          aria-label="Zamknij formularz opinii"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                      <textarea
                        id="kig-chat-feedback"
                        value={feedbackComment}
                        onChange={(event) =>
                          setFeedbackComment(event.target.value.slice(0, 500))
                        }
                        rows="2"
                        maxLength="500"
                        placeholder="Proszę krótko wskazać, czego zabrakło."
                      />
                      <button
                        type="submit"
                        className="kig-chat__feedback-submit"
                        disabled={
                          !feedbackComment.trim() ||
                          feedbackComment.trim() ===
                            conversationFeedback.comment
                        }
                      >
                        {conversationFeedback.comment
                          ? "Zaktualizuj opinię"
                          : "Wyślij komentarz"}
                      </button>
                    </form>
                  )}
              </aside>
            )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {activeView === CHAT_VIEW.QUESTIONS && (
            <nav
              id="kig-chat-question-picker"
              className="kig-chat__question-panel"
              aria-label="Pytania w Pomocy AI"
            >
              <div className="kig-chat__question-catalog">
                {messages.length === 0 && (
                  <div className="kig-chat__question-intro">
                    <div
                      className="kig-chat__question-intro-icon"
                      aria-hidden="true"
                    >
                      <i className="bi bi-chat-square-dots" />
                    </div>
                    <div>
                      <strong>W czym możemy pomóc?</strong>
                      <span>
                        Wybierz pytanie dotyczące bieżącego etapu wniosku.
                      </span>
                    </div>
                  </div>
                )}
                {questionSet.questions.length === 0 ? (
                  <div className="kig-chat__question-empty" role="status">
                    <i className="bi bi-info-circle" aria-hidden="true" />
                    <strong>Brak pytań dla tego etapu</strong>
                    <span>
                      Pytania dla tego statusu nie zostały jeszcze przygotowane.
                    </span>
                  </div>
                ) : selectedCategory ? (
                  <>
                    <button
                      type="button"
                      className="kig-chat__question-back"
                      onClick={() => setExpandedCategoryId(null)}
                    >
                      <i className="bi bi-arrow-left" aria-hidden="true" />
                      Wszystkie tematy
                    </button>
                    <div className="kig-chat__question-panel-heading kig-chat__question-panel-heading--category">
                      <i
                        className={`bi ${selectedCategory.icon}`}
                        aria-hidden="true"
                      />
                      <div>
                        <strong>{selectedCategory.label}</strong>
                        <span>Wybierz pytanie z tego tematu.</span>
                      </div>
                    </div>
                    <div className="kig-chat__topic-questions">
                      {selectedCategory.questions.map((question) =>
                        renderQuestionButton(question),
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="kig-chat__question-panel-heading">
                      <strong>Najczęściej wybierane:</strong>
                    </div>
                    <div className="kig-chat__featured-questions">
                      {questionSet.featuredQuestions.map((question) =>
                        renderQuestionButton(question, "featured"),
                      )}
                    </div>

                    {questionSet.categories.length > 0 && (
                      <div className="kig-chat__topics">
                        <span className="kig-chat__topics-label">
                          Lub wybierz z poniższych kategorii:
                        </span>
                        <div className="kig-chat__topic-grid">
                          {questionSet.categories.map((category) => (
                            <button
                              key={category.id}
                              type="button"
                              className="kig-chat__topic-tile"
                              onClick={() => setExpandedCategoryId(category.id)}
                              aria-label={`${category.label}, ${formatQuestionCount(
                                category.questions.length,
                              )}`}
                            >
                              <i
                                className={`bi ${category.icon}`}
                                aria-hidden="true"
                              />
                              <span>{category.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {messages.length > 0 && (
                <div className="kig-chat__question-view-toolbar">
                  <button
                    type="button"
                    className="kig-chat__question-view-back"
                    onClick={() => setActiveView(CHAT_VIEW.CONVERSATION)}
                  >
                    Wróć do rozmowy
                    <i className="bi bi-arrow-right" aria-hidden="true" />
                  </button>
                </div>
              )}
            </nav>
          )}

          {messages.length > 0 &&
            activeView === CHAT_VIEW.CONVERSATION && (
              <footer
                className={`kig-chat__question-launcher ${
                  isTyping ? "kig-chat__question-launcher--waiting" : ""
                }`}
              >
                <button
                  type="button"
                  className="kig-chat__question-launcher-button"
                  onClick={() => {
                    setExpandedCategoryId(null);
                    setActiveView(CHAT_VIEW.QUESTIONS);
                  }}
                  disabled={isTyping || questionSet.questions.length === 0}
                >
                  {!isTyping && (
                    <i className="bi bi-arrow-left" aria-hidden="true" />
                  )}
                  <span>
                    {isTyping
                      ? "Trwa generowanie odpowiedzi, proszę czekać"
                      : "Wróć do pytań"}
                  </span>
                </button>
              </footer>
            )}

          {isEndConversationConfirmOpen && (
            <div className="kig-chat__dialog-backdrop">
              <div
                className="kig-chat__end-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="kig-chat-end-title"
                aria-describedby="kig-chat-end-description"
              >
                <div className="kig-chat__end-dialog-icon" aria-hidden="true">
                  <i
                    className={
                      "bi " +
                      (isEndConversationFeedbackStep
                        ? "bi-hand-thumbs-up"
                        : "bi-chat-square-text")
                    }
                  />
                </div>
                <h3 id="kig-chat-end-title">
                  {isEndConversationFeedbackStep
                    ? "Oceń rozmowę"
                    : "Zakończyć rozmowę?"}
                </h3>
                <p id="kig-chat-end-description">
                  {isEndConversationFeedbackStep
                    ? "Dziękujemy. Przed zamknięciem możesz jeszcze ocenić udzieloną pomoc."
                    : "Po zakończeniu kolejna wiadomość rozpocznie nową sesję. Bieżąca rozmowa nie będzie już wyświetlana w tym oknie."}
                </p>

                {isEndConversationFeedbackStep && (
                  <div className="kig-chat__end-feedback">
                    <span className="kig-chat__end-feedback-question">
                      Czy ta rozmowa była pomocna?
                    </span>
                    <div className="kig-chat__feedback-actions">
                      <button
                        type="button"
                        className={
                          "kig-chat__feedback-rating " +
                          (conversationFeedback.rating === "positive"
                            ? "kig-chat__feedback-rating--active"
                            : "")
                        }
                        onClick={() => handleFeedbackRating("positive")}
                        aria-label="Oceń rozmowę pozytywnie"
                        aria-pressed={
                          conversationFeedback.rating === "positive"
                        }
                        title="Rozmowa pomocna"
                      >
                        <i
                          className={
                            "bi " +
                            (conversationFeedback.rating === "positive"
                              ? "bi-hand-thumbs-up-fill"
                              : "bi-hand-thumbs-up")
                          }
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        className={
                          "kig-chat__feedback-rating " +
                          (conversationFeedback.rating === "negative"
                            ? "kig-chat__feedback-rating--active kig-chat__feedback-rating--negative"
                            : "")
                        }
                        onClick={() => handleFeedbackRating("negative")}
                        aria-label="Oceń rozmowę negatywnie"
                        aria-pressed={
                          conversationFeedback.rating === "negative"
                        }
                        title="Rozmowa wymaga poprawy"
                      >
                        <i
                          className={
                            "bi " +
                            (conversationFeedback.rating === "negative"
                              ? "bi-hand-thumbs-down-fill"
                              : "bi-hand-thumbs-down")
                          }
                          aria-hidden="true"
                        />
                      </button>
                    </div>

                    {conversationFeedback.rating === "negative" && (
                      <div className="kig-chat__end-feedback-comment">
                        <label htmlFor="kig-chat-end-feedback">
                          Co możemy poprawić? <span>(opcjonalnie)</span>
                        </label>
                        <textarea
                          id="kig-chat-end-feedback"
                          value={feedbackComment}
                          onChange={(event) =>
                            setFeedbackComment(event.target.value.slice(0, 500))
                          }
                          rows="2"
                          maxLength="500"
                          placeholder="Proszę krótko wskazać, czego zabrakło."
                        />
                      </div>
                    )}
                  </div>
                )}

                {endConversationError && (
                  <div className="kig-chat__end-dialog-error" role="alert">
                    {endConversationError}
                  </div>
                )}

                <div className="kig-chat__end-dialog-actions">
                  {isEndConversationFeedbackStep ? (
                    <>
                      <button
                        type="button"
                        className="kig-chat__end-dialog-cancel"
                        onClick={handleEndConversation}
                        disabled={isEndingConversation || isSavingEndFeedback}
                        autoFocus
                      >
                        Pomiń ocenę
                      </button>
                      <button
                        type="button"
                        className="kig-chat__end-dialog-confirm"
                        onClick={handleEndConversationAfterFeedback}
                        disabled={
                          !conversationFeedback.rating ||
                          isEndingConversation ||
                          isSavingEndFeedback
                        }
                      >
                        {isEndingConversation || isSavingEndFeedback
                          ? "Kończenie..."
                          : "Zapisz ocenę i zakończ"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="kig-chat__end-dialog-cancel"
                        onClick={() => {
                          setIsEndConversationConfirmOpen(false);
                          setEndConversationError("");
                        }}
                        disabled={isEndingConversation}
                        autoFocus
                      >
                        Wróć do rozmowy
                      </button>
                      <button
                        type="button"
                        className="kig-chat__end-dialog-confirm"
                        onClick={handleConfirmEndConversation}
                        disabled={isEndingConversation}
                      >
                        {isEndingConversation
                          ? "Kończenie..."
                          : "Zakończ rozmowę"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {!isOpen && (
        <button
          ref={launcherRef}
          type="button"
          className="kig-chat__launcher"
          onClick={() => setIsOpen(true)}
          aria-label="Otwórz Pomoc AI"
          title="Otwórz Pomoc AI"
          aria-controls="kig-chat-panel"
          aria-expanded={isOpen}
        >
          <span className="kig-chat__launcher-icon" aria-hidden="true">
            <i className="bi bi-stars" />
          </span>
        </button>
      )}
    </div>
  );
}

export default ChatWidget;
